import React from "react";
import { Row, Col, Card, Input, Table, Button, Typography, message, Popconfirm, Grid } from "antd";
import { DeleteOutlined, SaveOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import PackingQRParser from "./components/PackingQRParser";
import { setFormField, removePackingItem, resetPackingForm } from "./packingSlice";
import { savePacking } from "./packingThunk";

const { Title, Text } = Typography;

const validatePacking = (sscc_code, items) => {
    if (!sscc_code || !sscc_code.trim()) {
        return { valid: false, message: "SSCC Code is required" };
    }

    if (!items || items.length === 0) {
        return { valid: false, message: "Add at least one product" };
    }

    const seen = new Set();
    for (const item of items) {
        const key = `${item.gtin}::${item.serial_number}`;
        if (seen.has(key)) {
            return {
                valid: false,
                message: `Duplicate serial number found: ${item.serial_number}`,
            };
        }
        seen.add(key);
    }

    return { valid: true };
};

const Packing = () => {
    const dispatch = useDispatch();
    const { sscc_code, items, saving } = useSelector((s) => s.tatmeenPacking);
    const screens = Grid.useBreakpoint();
    const isMobile = !screens.md;

    const handleSave = async () => {
        const validation = validatePacking(sscc_code, items);
        if (!validation.valid) {
            message.error(validation.message);
            return;
        }

        const result = await dispatch(
            savePacking({
                sscc_code,
                items: items.map((it) => ({
                    manufacture_id: it.manufacture_id,
                    gtin: it.gtin,
                    serial_number: it.serial_number,
                    batch_name: it.batch_name,
                    batch_expiry: it.batch_expiry,
                    raw_qr_code: it.raw_qr_code,
                })),
            })
        );

        if (savePacking.fulfilled.match(result)) {
            message.success(result.payload?.message || "Packing saved successfully");
            dispatch(resetPackingForm());
        } else {
            message.error(result.payload || "Failed to save packing");
        }
    };

    const columns = [
        { title: "Manufacturer", dataIndex: "manufacturer_name", key: "manufacturer_name" },
        { title: "GTIN", dataIndex: "gtin", key: "gtin" },
        { title: "Serial Number", dataIndex: "serial_number", key: "serial_number" },
        { title: "Batch Name", dataIndex: "batch_name", key: "batch_name" },
        { title: "Batch Expiry", dataIndex: "batch_expiry", key: "batch_expiry" },
        {
            title: "Action",
            key: "action",
            render: (_, record, index) => (
                <Popconfirm
                    title="Remove this product?"
                    onConfirm={() => dispatch(removePackingItem(index))}
                >
                    <Button danger type="text" icon={<DeleteOutlined />} />
                </Popconfirm>
            ),
        },
    ];

    return (
        <div style={{ padding: isMobile ? 0 : 20 }}>
            <Title level={isMobile ? 4 : 3} style={{ margin: 0, marginBottom: 16 }}>
                Packing
            </Title>

            <Row gutter={[16, 16]}>
                <Col xs={24} md={10} lg={8}>
                    <PackingQRParser />
                </Col>

                <Col xs={24} md={14} lg={16}>
                    <Card
                        bordered={false}
                        style={{ borderRadius: 12, boxShadow: "0 2px 12px rgba(0,0,0,.08)" }}
                        styles={{ body: { padding: isMobile ? 12 : 24 } }}
                    >
                        <Text strong>SSCC Code</Text>
                        <Input
                            style={{ marginTop: 5, marginBottom: 16 }}
                            placeholder="Enter SSCC Code"
                            value={sscc_code}
                            onChange={(e) =>
                                dispatch(setFormField({ field: "sscc_code", value: e.target.value }))
                            }
                        />

                        <Table
                            rowKey={(_, index) => index}
                            columns={columns}
                            dataSource={items}
                            pagination={false}
                            size="small"
                            scroll={{ x: "max-content" }}
                            locale={{ emptyText: "No products added yet" }}
                        />

                        <Button
                            type="primary"
                            icon={<SaveOutlined />}
                            loading={saving}
                            onClick={handleSave}
                            block={isMobile}
                            style={{ marginTop: 16 }}
                        >
                            Save Packing
                        </Button>
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default Packing;
